import {PeopleIcon, ProjectIcon, ProjectRoadmapIcon, TableIcon, ToolsIcon} from '@primer/octicons-react'
import {useCallback, useMemo} from 'react'
import invariant from 'tiny-invariant'

import {omit} from '../../utils/omit'
import {apiApplyTemplate} from '../api/memex/api-apply-template'
import {DefaultOmitPropertiesForView, ViewTypeParam} from '../api/view/contracts'
import {GroupEnum, SelectionOptionEnum, type SidePanelGroup} from '../components/template-dialog/types'
import {useUpdateMemexTitle} from '../state-providers/memex/use-update-memex-title'
import {useApiRequest} from './use-api-request'
import {useEnabledFeatures} from './use-enabled-features'
import {useUpdateViewWithoutSettingStates} from './use-view-apis'
import {ViewStateActionTypes} from './use-view-state-reducer/view-state-action-types'
import {useViews} from './use-views'

export type ApplyTemplateCallback = (params: {selectedTemplate: string; title: string; rollback?: () => void}) => void

export const useTemplateSideSelection = () => {
  const {viewStateDispatch, viewsMap} = useViews()
  const updateViewWithoutSettingStates = useUpdateViewWithoutSettingStates()
  const {memex_table_without_limits} = useEnabledFeatures()
  const {updateTitle} = useUpdateMemexTitle()
  const applyTemplateRequest = useApiRequest({
    request: async (selectedTemplate: string) => {
      await apiApplyTemplate({template: selectedTemplate})
    },
    rollback: (rollbackAction?: () => void) => {
      if (rollbackAction) {
        rollbackAction()
      }
    },
    showErrorToast: true,
  })

  const handleApplyTemplate = useCallback<ApplyTemplateCallback>(
    async ({selectedTemplate, title, rollback}) => {
      await Promise.all([applyTemplateRequest.perform(selectedTemplate, rollback), updateTitle(title)])
    },
    [applyTemplateRequest, updateTitle],
  )

  const handleApplyTable = useCallback<ApplyTemplateCallback>(
    async ({title}) => {
      await updateTitle(title)
    },
    [updateTitle],
  )

  const handleApplyBoard = useCallback<ApplyTemplateCallback>(
    async ({title}) => {
      const view = viewsMap[1]
      invariant(view, 'view must exist')
      const nextServerViewState = {
        ...view.serverViewState,
        layout: ViewTypeParam.Board,
      }

      // Update to board layout for the current view immediately.
      await Promise.all([
        updateViewWithoutSettingStates.perform({
          viewNumber: view.number,
          view: omit(nextServerViewState, DefaultOmitPropertiesForView),
        }),
        updateTitle(title),
      ])
      if (updateViewWithoutSettingStates.status.current.status === 'succeeded') {
        viewStateDispatch({
          type: ViewStateActionTypes.UpdateView,
          view: nextServerViewState,
        })
      }
    },
    [updateTitle, updateViewWithoutSettingStates, viewStateDispatch, viewsMap],
  )

  const handleApplyRoadmap = useCallback<ApplyTemplateCallback>(
    async ({title}) => {
      const view = viewsMap[1]
      invariant(view, 'view must exist')
      const nextServerViewState = {
        ...view.serverViewState,
        layout: ViewTypeParam.Roadmap,
      }

      // Update to roadmap layout for the current view immediately.
      await Promise.all([
        updateViewWithoutSettingStates.perform({
          viewNumber: view.number,
          view: omit(nextServerViewState, DefaultOmitPropertiesForView),
        }),
        updateTitle(title),
      ])
      if (updateViewWithoutSettingStates.status.current.status === 'succeeded') {
        viewStateDispatch({
          type: ViewStateActionTypes.UpdateView,
          view: nextServerViewState,
        })
      }
    },
    [updateTitle, updateViewWithoutSettingStates, viewStateDispatch, viewsMap],
  )

  const blankTemplates = useMemo(() => {
    return [
      {
        key: SelectionOptionEnum.table,
        groupKey: GroupEnum.layout,
        title: 'Table',
        icon: TableIcon,
        action: handleApplyTable,
        template: undefined,
      },
      {
        key: SelectionOptionEnum.board,
        groupKey: GroupEnum.layout,
        title: 'Board',
        icon: ProjectIcon,
        action: handleApplyBoard,
        template: undefined,
        disabled: memex_table_without_limits,
      },
      {
        key: SelectionOptionEnum.roadmap,
        groupKey: GroupEnum.layout,
        title: 'Roadmap',
        icon: ProjectRoadmapIcon,
        action: handleApplyRoadmap,
        template: undefined,
        disabled: memex_table_without_limits,
      },
    ]
  }, [handleApplyBoard, handleApplyRoadmap, handleApplyTable, memex_table_without_limits])

  const sidePanelGroups: [SidePanelGroup, SidePanelGroup] = [
    {
      key: GroupEnum.layout,
      groupTitle: 'Start from scratch',
      items: blankTemplates,
    },
    {
      key: GroupEnum.functionality,
      groupTitle: 'Project templates',
      items: [
        {
          key: SelectionOptionEnum.backlog,
          groupKey: GroupEnum.functionality,
          title: 'Team backlog',
          icon: PeopleIcon,
          action: handleApplyTemplate,
          template: undefined,
        },
        {
          key: SelectionOptionEnum.feature,
          groupKey: GroupEnum.functionality,
          title: 'Feature',
          icon: ToolsIcon,
          action: handleApplyTemplate,
          template: undefined,
        },
      ],
    },
  ]

  return sidePanelGroups
}
